import React from 'react';
import type { DiaperModel } from '../../lib/types';

interface InspectorToolbarProps {
  selectedDiaperCounter: number | null;
  currentDiaperModel: DiaperModel | null;
  overriddenDiaperModelCode: number | null;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setTimelineFilterPreset: (minutes: number) => void;
  useDynamicRange: boolean;
  setUseDynamicRange: (v: boolean) => void;
  onOpenLiquidForm: () => void;
  onOpenAnnotationForm: () => void;
  dataResolution: number;
  onResolutionChange: (value: number) => void;
  useSideBySideLayout: boolean;
  setUseSideBySideLayout: (v: boolean) => void;
}

export const InspectorToolbar: React.FC<InspectorToolbarProps> = ({
  selectedDiaperCounter,
  currentDiaperModel,
  overriddenDiaperModelCode,
  zoomIn,
  zoomOut,
  resetZoom,
  setTimelineFilterPreset,
  useDynamicRange,
  setUseDynamicRange,
  onOpenLiquidForm,
  onOpenAnnotationForm,
  dataResolution,
  onResolutionChange,
  useSideBySideLayout,
  setUseSideBySideLayout,
}) => (
  <div className="mt-8 pt-6 pb-4 border-t-2 border-gray-300 sticky top-4 z-30 bg-gray-50">
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex flex-wrap items-center gap-6">
        {selectedDiaperCounter !== null && (
          <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-md border border-blue-100">
            <span className="text-sm font-bold text-blue-900">
              Diaper #{selectedDiaperCounter}
            </span>
            {currentDiaperModel && (
              <span className="text-xs text-blue-700 font-medium">
                • {currentDiaperModel.name.replace(/^MediSens Abena /i, '')} ({overriddenDiaperModelCode !== null ? '🔧' : ''}{currentDiaperModel.code})
              </span>
            )}
          </div>
        )}

        {selectedDiaperCounter !== null && (
          <div className="h-8 w-px bg-gray-300"></div>
        )}

        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold text-gray-700">
            Timeline Zoom:
          </label>
          <button
            onClick={zoomIn}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm font-medium"
            title="Zoom In (50%)"
          >
            🔍+ Zoom In
          </button>
          <button
            onClick={zoomOut}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm font-medium"
            title="Zoom Out (200%)"
          >
            🔍- Zoom Out
          </button>
          <button
            onClick={resetZoom}
            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm font-medium"
            title="Show All Data"
          >
            ↔️ Show All
          </button>
        </div>

        <div className="h-8 w-px bg-gray-300"></div>

        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold text-gray-700">
            Quick Presets:
          </label>
          <button onClick={() => setTimelineFilterPreset(5)} className="px-3 py-1 rounded text-sm bg-gray-100 text-gray-800 hover:bg-gray-200">Last 5m</button>
          <button onClick={() => setTimelineFilterPreset(10)} className="px-3 py-1 rounded text-sm bg-gray-100 text-gray-800 hover:bg-gray-200">Last 10m</button>
          <button onClick={() => setTimelineFilterPreset(30)} className="px-3 py-1 rounded text-sm bg-gray-100 text-gray-800 hover:bg-gray-200">Last 30m</button>
        </div>

        <div className="h-8 w-px bg-gray-300"></div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useDynamicRange}
              onChange={(e) => setUseDynamicRange(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Show only Dynamic Range
            </span>
          </label>
          <span className="text-xs text-gray-500">(Affects both graphs)</span>
        </div>

        <div className="h-8 w-px bg-gray-300"></div>

        <button
          onClick={onOpenLiquidForm}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium shadow-sm"
        >
          <span>💧</span>
          <span>Add Liquid</span>
        </button>

        <button
          onClick={onOpenAnnotationForm}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2 font-medium shadow-sm"
        >
          <span>📌</span>
          <span>Add Annotation</span>
        </button>

        <div className="h-8 w-px bg-gray-300"></div>

        <div className="flex flex-col gap-1 w-32">
          <div className="flex justify-between items-center">
            <label className="text-xs font-medium text-gray-700">Data Resolution</label>
            <span className="text-xs text-gray-500 font-mono">{dataResolution}%</span>
          </div>
          <input
            type="range"
            min="1"
            max="100"
            step="1"
            value={dataResolution}
            onChange={(e) => onResolutionChange(parseInt(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            title={`Show ${dataResolution}% of measurements (for performance)`}
          />
        </div>

        <div className="h-8 w-px bg-gray-300 hidden xl:block"></div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useSideBySideLayout}
              onChange={(e) => setUseSideBySideLayout(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Side-by-Side Layout
            </span>
          </label>
          <span className="text-xs text-gray-500">(Wide screens only)</span>
        </div>
      </div>
    </div>
  </div>
);
